import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useUIStore } from '@/lib/store/ui-store'
import { useRoomStore, type RoomSummary } from '@/lib/store/room-store'
import { useThreadStore, selectAnyThreadUnread } from '@/lib/store/thread-store'
import { Avatar } from '@/components/ui/Avatar'
import { BridgeBadge } from '@/components/ui/BridgeBadge'
import { getClient } from '@/lib/matrix/client'
import { getSpaces } from '@/lib/matrix/spaces'

interface SidebarProps {
  onCreateRoom?: () => void
  onNewDirectMessage?: () => void
}

type Category = 'channel' | 'dm' | 'app'

interface Classified extends RoomSummary {
  category: Category
}

// Rooms whose only counterpart is a bridge bot / feed (Telegram channels, bot
// chats) land in "Apps & feeds": bridged, not a DM, and effectively 1–2 members.
function classify(room: RoomSummary): Category {
  if (room.isDirect) return 'dm'
  const count = getClient()?.getRoom(room.roomId)?.getJoinedMemberCount() ?? 99
  if (room.bridgeType && count <= 2) return 'app'
  return 'channel'
}

export function Sidebar({ onCreateRoom, onNewDirectMessage }: SidebarProps) {
  const { sidebarOpen, setSidebarOpen, setThreadsViewOpen } = useUIStore()
  const navigate = useNavigate()
  const rooms = useRoomStore((s) => s.rooms)
  const activeRoomId = useRoomStore((s) => s.activeRoomId)
  const setActiveRoom = useRoomStore((s) => s.setActiveRoom)
  const anyThreadUnread = useThreadStore(selectAnyThreadUnread)
  const [q, setQ] = useState('')
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null)
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false)

  const { spaces, spaceIds } = useMemo(() => {
    const client = getClient()
    return client ? getSpaces(client) : { spaces: [], spaceIds: new Set<string>() }
  }, [rooms])

  const selectedSpace = spaces.find((s) => s.roomId === selectedSpaceId)
  const spaceChildIds = selectedSpace ? new Set(selectedSpace.childIds) : null

  const { channels, dms, apps } = useMemo(() => {
    const lower = q.trim().toLowerCase()
    const list = Array.from(rooms.values())
      .filter((r) => r.membership === 'join' && !spaceIds.has(r.roomId))
      .filter((r) => !spaceChildIds || spaceChildIds.has(r.roomId))
      .filter((r) => !lower || r.name.toLowerCase().includes(lower))
      .map((r): Classified => ({ ...r, category: classify(r) }))
      .sort((a, b) => (b.lastEvent?.timestamp ?? 0) - (a.lastEvent?.timestamp ?? 0))
    return {
      channels: list.filter((r) => r.category === 'channel'),
      dms: list.filter((r) => r.category === 'dm'),
      apps: list.filter((r) => r.category === 'app'),
    }
  }, [rooms, q, spaceIds, spaceChildIds])

  const handleSelect = (roomId: string) => {
    setActiveRoom(roomId)
    navigate(`/room/${encodeURIComponent(roomId)}`)
    setSidebarOpen(false)
  }

  return (
    <>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[280px] flex flex-col overflow-hidden sidebar-slack
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="sb-inner">
          {/* Workspace header + Space switcher */}
          <div className="slk-workspace">
            <div className="relative">
              <button className="slk-ws-name" onClick={() => spaces.length && setSpaceMenuOpen((o) => !o)}>
                <span>{selectedSpace ? selectedSpace.name : 'ChatPulse'}</span>
                {spaces.length > 0 && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(90deg)' }}><polyline points="9 18 15 12 9 6" /></svg>
                )}
              </button>
              {spaceMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSpaceMenuOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 w-56 py-1 bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg">
                    <SpaceMenuItem label="Home — all conversations" active={!selectedSpaceId} onClick={() => { setSelectedSpaceId(null); setSpaceMenuOpen(false) }} />
                    {spaces.map((s) => (
                      <SpaceMenuItem key={s.roomId} label={s.name} active={s.roomId === selectedSpaceId} onClick={() => { setSelectedSpaceId(s.roomId); setSpaceMenuOpen(false) }} />
                    ))}
                  </div>
                </>
              )}
            </div>
            <button className="slk-ws-compose" title="New message" onClick={onNewDirectMessage}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: '0 12px 8px' }}>
            <div className="slk-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ChatPulse" />
            </div>
          </div>

          <div className="scroll" style={{ flex: 1, overflowY: 'auto', padding: '2px 8px 16px' }}>
            {/* Global Threads entry — independent of any single room's unread, so
                thread activity stays visible wherever you are in the timeline. */}
            <button
              className={`slk-row${anyThreadUnread ? ' slk-unread' : ''}`}
              onClick={() => setThreadsViewOpen(true)}
              title="Threads"
            >
              <span className="slk-lead">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><line x1="8" y1="9" x2="16" y2="9" /><line x1="8" y1="13" x2="13" y2="13" /></svg>
              </span>
              <span className="slk-name">Threads</span>
              {anyThreadUnread && <span className="slk-unread-dot" title="Unread thread replies" />}
            </button>

            <Section label="Channels" rooms={channels} activeId={activeRoomId} onSelect={handleSelect} onAdd={onCreateRoom} addTitle="Create room" />
            <Section label="Direct messages" rooms={dms} activeId={activeRoomId} onSelect={handleSelect} onAdd={onNewDirectMessage} addTitle="New message" />
            {apps.length > 0 && (
              <Section label="Apps & feeds" rooms={apps} activeId={activeRoomId} onSelect={handleSelect} />
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

function SpaceMenuItem({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 text-sm truncate transition-colors hover:bg-[rgb(var(--np-raised))] ${active ? 'text-[rgb(var(--np-accent))]' : 'text-[rgb(var(--np-tx-secondary))]'}`}
    >
      {label}
    </button>
  )
}

function Section({ label, rooms, activeId, onSelect, onAdd, addTitle }: {
  label: string
  rooms: Classified[]
  activeId: string | null
  onSelect: (roomId: string) => void
  onAdd?: () => void
  addTitle?: string
}) {
  const [open, setOpen] = useState(true)
  if (rooms.length === 0 && !onAdd) return null
  return (
    <div style={{ marginTop: 10 }}>
      <div className="slk-sec-head">
        <button className="slk-sec-toggle" onClick={() => setOpen((o) => !o)}>
          <span className="slk-caret" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </span>
          {label}
        </button>
        {onAdd && (
          <button className="slk-sec-add" title={addTitle} onClick={onAdd}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
        )}
      </div>
      {open && rooms.map((r) => <Row key={r.roomId} room={r} active={r.roomId === activeId} onClick={() => onSelect(r.roomId)} />)}
    </div>
  )
}

function Row({ room, active, onClick }: { room: Classified; active: boolean; onClick: () => void }) {
  const unread = room.unreadCount > 0
  const quietUnread = !unread && room.hasUnread
  return (
    <button onClick={onClick} className={`slk-row${active ? ' slk-active' : ''}${unread || quietUnread ? ' slk-unread' : ''}`}>
      <span className="slk-lead">
        {room.avatarUrl ? (
          // Show the room/DM avatar whenever there is one — even for channels.
          <Avatar mxc={room.avatarUrl} name={room.name} userId={room.roomId} size={20} square />
        ) : room.category === 'channel' ? (
          room.isEncrypted ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>
          )
        ) : (
          <Avatar mxc={null} name={room.name} userId={room.roomId} size={20} square />
        )}
      </span>
      <span className="slk-name">{room.name}</span>
      {room.bridgeType && <span className="slk-bridge"><BridgeBadge type={room.bridgeType} size={12} /></span>}
      {unread && <span className="slk-badge font-data">{room.unreadCount > 99 ? '99+' : room.unreadCount}</span>}
      {quietUnread && <span className="slk-unread-dot" title="Unread messages" />}
    </button>
  )
}
